import React, { useEffect, useState } from 'react';
import {
  Paper,
  Box,
  Stack,
  Typography,
  Chip,
  Divider,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Grid,
  Avatar,
  Badge,
  IconButton,
  Tooltip,
  CircularProgress,
  InputAdornment,
  useTheme,
  Snackbar,
  Alert,
  Card,
  CardContent
} from '@mui/material';
import {
  MdAssignment,
  MdSearch,
  MdClear,
  MdEmail,
  MdInfo,
  MdPendingActions,
  MdVisibility,
  MdThumbUp,
  MdMoreVert,
  MdFolder,
  MdCheckCircle,
  MdUpload,
  MdSchedule,
  MdClose,
  MdAdminPanelSettings,
  MdDownload,
  MdLock
} from 'react-icons/md';
import { green, blue, purple, orange, grey, red } from '@mui/material/colors';
import { motion } from 'framer-motion';
import { AssignedDocument } from '../types';
import axiosInstance from 'Services/APIService';
import { useDocumentManagement } from '../DocumentManagementContext';

interface DocumentsListProps {
  filteredDocuments: AssignedDocument[];
  selectedCategory: string;
  selectedSubcategory: string;
  searchTerm: string;
  statusFilter: string;
  isMobile: boolean;
  actionLoading: string | null;
  acceptDeleteReassignLoading: boolean;
  getCategoryColor: (category: string) => string;
  getDocumentStatus: (document: AssignedDocument) => string;
  formatDate: (date: string) => string;
  handleSearch: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleStatusFilterChange: (event: any) => void;
  handleClearFilters: () => void;
  handlePreviewDocument: (document: AssignedDocument) => void;
  handleDocumentAction: (
    document: AssignedDocument,
    action: 'approve' | 'delete' | 'reassign'
  ) => Promise<void>;
  handleActionMenuOpen: (event: React.MouseEvent<HTMLElement>, document: AssignedDocument) => void;
  statusColors: Record<string, string>;
  statusIcons: Record<string, React.ReactElement>;
  statusLabels: Record<string, string>;
  typography: any;
}

interface SnackbarState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'warning' | 'info';
}

const AssignedList: React.FC<DocumentsListProps> = ({
  filteredDocuments,
  selectedCategory,
  selectedSubcategory,
  searchTerm,
  statusFilter,
  isMobile,
  actionLoading,
  acceptDeleteReassignLoading,
  getCategoryColor,
  getDocumentStatus,
  formatDate,
  handleSearch,
  handleStatusFilterChange,
  handleClearFilters,
  handlePreviewDocument,
  handleDocumentAction,
  handleActionMenuOpen,
  statusColors,
  statusIcons,
  statusLabels,
  typography
}) => {
  const theme = useTheme();
  const [avatarUrls, setAvatarUrls] = useState<{ [key: string]: string | null }>({});
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'success'
  });
  const [downloadLoading, setDownloadLoading] = useState<string | null>(null);

  const { isAdminLogin } = useDocumentManagement();

  // Show snackbar notification
  const showNotification = (
    message: string,
    severity: 'success' | 'error' | 'warning' | 'info'
  ) => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  // Close snackbar
  const handleCloseSnackbar = (_event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  // Document download function
  const handleDocumentDownload = async (document: AssignedDocument) => {
    if (!document.uploadedFile || !document.uploadedFile.filePath) {
      showNotification('No file available for download', 'warning');
      return;
    }

    const downloadKey = `download-${document.documentId}`;
    setDownloadLoading(downloadKey);

    try {
      const response = await axiosInstance.get(
        `/documents/download?path=${encodeURIComponent(document.uploadedFile.filePath)}`,
        {
          responseType: 'blob'
        }
      );

      const blob = new Blob([response.data], {
        type: document.uploadedFile.fileType || 'application/octet-stream'
      });
      const url = window.URL.createObjectURL(blob);

      const link = window.document.createElement('a');
      link.href = url;
      link.download = document.uploadedFile.fileName || `${document.documentName}.pdf`;
      window.document.body.appendChild(link);
      link.click();

      window.document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      showNotification(`Document "${document.documentName}" downloaded successfully!`, 'success');
    } catch (error: any) {
      console.error('Download error:', error);
      const errorMessage = error?.response?.data?.message || 'Failed to download document';
      showNotification(`Download failed: ${errorMessage}`, 'error');
    } finally {
      setDownloadLoading(null);
    }
  };

  // Load avatars for documents
  useEffect(() => {
    const fetchAvatars = async () => {
      const newAvatarUrls: { [key: string]: string | null } = {};
      for (const document of filteredDocuments || []) {
        const employeeKey = document.assignedEmployeeMailId || document.assignedEmpName;
        if (document.avatar && employeeKey) {
          try {
            const response = await axiosInstance.get(
              `/avatar/get-avatar?path=${encodeURIComponent(document.avatar)}`,
              { responseType: 'blob' }
            );
            const imageUrl = URL.createObjectURL(response.data);
            newAvatarUrls[employeeKey] = imageUrl;
          } catch (error) {
            console.error(`Error fetching avatar for ${document.assignedEmpName}:`, error);
            newAvatarUrls[employeeKey] = null;
          }
        } else {
          if (employeeKey) {
            newAvatarUrls[employeeKey] = null;
          }
        }
      }
      setAvatarUrls(newAvatarUrls);
    };

    if (filteredDocuments && filteredDocuments.length > 0) {
      fetchAvatars();
    }

    return () => {
      Object.values(avatarUrls).forEach((url) => {
        if (url) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, [filteredDocuments]);

  // Enhanced document action handler with admin permission check
  const enhancedHandleDocumentAction = async (
    document: AssignedDocument,
    action: 'approve' | 'delete' | 'reassign'
  ) => {
    if (!isAdminLogin) {
      showNotification('Only administrators can perform this action', 'error');
      return;
    }

    if (document.isAccepted && action === 'approve') {
      showNotification('Document is already approved', 'warning');
      return;
    }

    try {
      await handleDocumentAction(document, action);
      switch (action) {
        case 'approve':
          showNotification(
            `Document "${document.documentName}" has been successfully approved!`,
            'success'
          );
          break;
        case 'delete':
          showNotification(
            `Document "${document.documentName}" has been successfully deleted!`,
            'success'
          );
          break;
        case 'reassign':
          showNotification(
            `Document "${document.documentName}" has been successfully reassigned!`,
            'success'
          );
          break;
      }
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || error?.message || 'An unexpected error occurred';
      switch (action) {
        case 'approve':
          showNotification(`Failed to approve document: ${errorMessage}`, 'error');
          break;
        case 'delete':
          showNotification(`Failed to delete document: ${errorMessage}`, 'error');
          break;
        case 'reassign':
          showNotification(`Failed to reassign document: ${errorMessage}`, 'error');
          break;
      }
    }
  };

  // MOBILE CARD LAYOUT
  const renderMobileCard = (document: AssignedDocument) => {
    const status = getDocumentStatus(document);
    const isActionLoading =
      actionLoading?.includes(document.documentId) || acceptDeleteReassignLoading;
    const isAccepted = document.isAccepted === true;
    const isSubmitted = document.isSubmitted === true;
    const downloadKey = `download-${document.documentId}`;
    const isDownloadLoading = downloadLoading === downloadKey;
    const employeeKey = document.assignedEmployeeMailId || document.assignedEmpName;
    const avatarUrl = avatarUrls[employeeKey];

    return (
      <motion.div
        key={`${document.documentId}-${document.assignedEmployeeMailId}`}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <Card
          elevation={3}
          sx={{
            mb: 2,
            borderRadius: 3,
            borderLeft: `6px solid ${
              isAccepted ? green[500] : isSubmitted ? blue[500] : orange[500]
            }`,
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: 6
            }
          }}
          onClick={() => handlePreviewDocument(document)}
        >
          <CardContent sx={{ p: 2.5 }}>
            {/* Header Section */}
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
              <Badge
                overlap="circular"
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                badgeContent={
                  <Box
                    sx={{
                      bgcolor: isAccepted ? green[500] : isSubmitted ? blue[500] : orange[500],
                      color: 'white',
                      borderRadius: '50%',
                      width: 20,
                      height: 20,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '2px solid white'
                    }}
                  >
                    {isAccepted ? (
                      <MdCheckCircle style={{ fontSize: 12 }} />
                    ) : isSubmitted ? (
                      <MdUpload style={{ fontSize: 12 }} />
                    ) : (
                      <MdPendingActions style={{ fontSize: 12 }} />
                    )}
                  </Box>
                }
              >
                <Avatar
                  src={avatarUrl || undefined}
                  sx={{
                    width: 60,
                    height: 60,
                    bgcolor: isAccepted ? green[500] : isSubmitted ? blue[500] : orange[500],
                    fontSize: '1.2rem'
                  }}
                >
                  {!avatarUrl &&
                    document.assignedEmpName
                      ?.split(' ')
                      .map((n) => n[0])
                      .join('')}
                </Avatar>
              </Badge>

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 700,
                    fontSize: '0.875rem',
                    mb: 0.5,
                    color: 'text.primary'
                  }}
                  noWrap
                >
                  {document.documentName}
                  {isAccepted && (
                    <MdLock
                      style={{
                        fontSize: 14,
                        marginLeft: 6,
                        color: green[500]
                      }}
                    />
                  )}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: 'text.primary',
                    mb: 0.5
                  }}
                  noWrap
                >
                  {document.assignedEmpName}
                </Typography>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <MdEmail style={{ fontSize: 12, color: grey[500] }} />
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary'
                    }}
                    noWrap
                  >
                    {document.assignedEmployeeMailId}
                  </Typography>
                </Stack>
              </Box>
            </Stack>

            {/* Document Details */}
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  mb: 1
                }}
              >
                Assigned: {formatDate(document.assignedDate)}
              </Typography>

              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                {document.uploadedFile && (
                  <Chip
                    label={document.uploadedFile.fileType?.toUpperCase() || 'FILE'}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: '0.625rem',
                      bgcolor: `${blue[500]}12`,
                      color: blue[600],
                      fontWeight: 500
                    }}
                  />
                )}
                {document.inputFields && document.inputFields.length > 0 && (
                  <Chip
                    icon={<MdInfo style={{ fontSize: 10 }} />}
                    label={`${document.inputFields.length} fields`}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: '0.625rem',
                      bgcolor: `${purple[500]}12`,
                      color: purple[600],
                      fontWeight: 500
                    }}
                  />
                )}
              </Stack>
            </Box>

            {/* Status and Actions Row */}
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ pt: 1, borderTop: `1px solid ${theme.palette.divider}` }}
            >
              <Chip
                label={isAccepted ? 'Approved' : isSubmitted ? 'Submitted' : 'Pending'}
                sx={{
                  height: 26,
                  fontSize: '0.6875rem',
                  fontWeight: 600,
                  bgcolor: isAccepted
                    ? `${green[500]}15`
                    : isSubmitted
                    ? `${blue[500]}15`
                    : `${orange[500]}15`,
                  color: isAccepted ? green[700] : isSubmitted ? blue[700] : orange[700]
                }}
              />

              <Stack direction="row" spacing={1}>
                <Tooltip title="Preview Document">
                  <IconButton
                    size="medium"
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePreviewDocument(document);
                    }}
                    sx={{
                      color: blue[600],
                      bgcolor: `${blue[500]}10`,
                      width: 38,
                      height: 38,
                      '&:hover': { bgcolor: `${blue[500]}20` }
                    }}
                  >
                    <MdVisibility size={16} />
                  </IconButton>
                </Tooltip>

                {document.uploadedFile && document.uploadedFile.filePath && (
                  <Tooltip title="Download Document">
                    <IconButton
                      size="medium"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDocumentDownload(document);
                      }}
                      disabled={isDownloadLoading}
                      sx={{
                        color: purple[600],
                        bgcolor: `${purple[500]}10`,
                        width: 38,
                        height: 38,
                        '&:hover': { bgcolor: `${purple[500]}20` }
                      }}
                    >
                      {isDownloadLoading ? (
                        <CircularProgress size={16} />
                      ) : (
                        <MdDownload size={16} />
                      )}
                    </IconButton>
                  </Tooltip>
                )}

                {isAdminLogin && isSubmitted && !isAccepted && (
                  <Tooltip title="Approve Document">
                    <IconButton
                      size="medium"
                      onClick={(e) => {
                        e.stopPropagation();
                        enhancedHandleDocumentAction(document, 'approve');
                      }}
                      disabled={isActionLoading}
                      sx={{
                        color: green[600],
                        bgcolor: `${green[500]}10`,
                        width: 38,
                        height: 38,
                        '&:hover': { bgcolor: `${green[500]}20` }
                      }}
                    >
                      {actionLoading === `approve-${document.documentId}` ? (
                        <CircularProgress size={16} />
                      ) : (
                        <MdThumbUp size={16} />
                      )}
                    </IconButton>
                  </Tooltip>
                )}

                {isAdminLogin && (
                  <Tooltip title="More Actions">
                    <IconButton
                      size="medium"
                      onClick={(e) => handleActionMenuOpen(e, document)}
                      disabled={acceptDeleteReassignLoading}
                      sx={{
                        color: grey[600],
                        bgcolor: `${grey[500]}10`,
                        width: 38,
                        height: 38,
                        '&:hover': { bgcolor: `${grey[500]}20` }
                      }}
                    >
                      {acceptDeleteReassignLoading ? (
                        <CircularProgress size={16} />
                      ) : (
                        <MdMoreVert size={16} />
                      )}
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </motion.div>
    );
  };

  // DESKTOP LIST LAYOUT
  const renderDesktopList = () => {
    return (
      <Box>
        {/* List Items */}
        {filteredDocuments.map((document) => {
          const status = getDocumentStatus(document);
          const isActionLoading =
            actionLoading?.includes(document.documentId) || acceptDeleteReassignLoading;
          const isAccepted = document.isAccepted === true;
          const isSubmitted = document.isSubmitted === true;
          const downloadKey = `download-${document.documentId}`;
          const isDownloadLoading = downloadLoading === downloadKey;
          const employeeKey = document.assignedEmployeeMailId || document.assignedEmpName;
          const avatarUrl = avatarUrls[employeeKey];

          return (
            <motion.div
              key={`${document.documentId}-${document.assignedEmployeeMailId}`}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  mb: 1,
                  borderRadius: 1.5,
                  border: `1px solid ${theme.palette.divider}`,
                  borderLeft: `4px solid ${
                    isAccepted ? green[500] : isSubmitted ? blue[500] : orange[500]
                  }`,
                  backgroundColor: 'background.paper',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    boxShadow: 2,
                    borderColor: isAccepted ? green[500] : isSubmitted ? blue[500] : orange[500],
                    transform: 'translateY(-1px)'
                  }
                }}
                onClick={() => handlePreviewDocument(document)}
              >
                <Grid container spacing={2} alignItems="center">
                  {/* Employee Info */}
                  <Grid item xs={12} sm={2.5}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Badge
                        overlap="circular"
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        badgeContent={
                          isAccepted ? (
                            <Box
                              sx={{
                                bgcolor: green[500],
                                color: 'white',
                                borderRadius: '50%',
                                width: 12,
                                height: 12,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                            >
                              <MdCheckCircle style={{ fontSize: 8 }} />
                            </Box>
                          ) : isSubmitted ? (
                            <Box
                              sx={{
                                bgcolor: blue[500],
                                color: 'white',
                                borderRadius: '50%',
                                width: 12,
                                height: 12,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                            >
                              <MdUpload style={{ fontSize: 8 }} />
                            </Box>
                          ) : (
                            <Box
                              sx={{
                                bgcolor: orange[500],
                                color: 'white',
                                borderRadius: '50%',
                                width: 12,
                                height: 12,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                            >
                              <MdPendingActions style={{ fontSize: 8 }} />
                            </Box>
                          )
                        }
                      >
                        <Avatar
                          src={avatarUrl || undefined}
                          sx={{
                            bgcolor: isAccepted
                              ? green[500]
                              : isSubmitted
                              ? blue[500]
                              : orange[500],
                            width: 32,
                            height: 32,
                            fontSize: '0.625rem'
                          }}
                        >
                          {!avatarUrl &&
                            document.assignedEmpName
                              ?.split(' ')
                              .map((n) => n[0])
                              .join('')}
                        </Avatar>
                      </Badge>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            lineHeight: 1.3,
                            color: 'text.primary',
                            fontSize: '0.6875rem'
                          }}
                          noWrap
                        >
                          {document.assignedEmpName}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'text.secondary',
                            lineHeight: 1.2,
                            display: 'flex',
                            alignItems: 'center'
                          }}
                          noWrap
                        >
                          <MdEmail style={{ fontSize: 10, marginRight: 2 }} />
                          {document.assignedEmployeeMailId}
                        </Typography>
                      </Box>
                    </Stack>
                  </Grid>

                  {/* Document Info */}
                  <Grid item xs={12} sm={3}>
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          mb: 0.5,
                          lineHeight: 1.3,
                          color: 'text.primary',
                          fontSize: '0.6875rem'
                        }}
                      >
                        {document.documentName}
                        {isAccepted && (
                          <MdLock
                            style={{
                              fontSize: 12,
                              marginLeft: 4,
                              color: green[500]
                            }}
                          />
                        )}
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'text.secondary'
                          }}
                        >
                          Assigned: {formatDate(document.assignedDate)}
                        </Typography>
                        {document.uploadedFile && (
                          <Chip
                            label={document.uploadedFile.fileType?.toUpperCase() || 'FILE'}
                            size="small"
                            sx={{
                              height: 16,
                              fontSize: '0.5625rem',
                              bgcolor: `${blue[500]}15`,
                              color: blue[600],
                              '& .MuiChip-label': {
                                px: 0.5
                              }
                            }}
                          />
                        )}
                        {document.inputFields && document.inputFields.length > 0 && (
                          <Chip
                            icon={<MdInfo style={{ fontSize: 10 }} />}
                            label={`${document.inputFields.length} fields`}
                            size="small"
                            sx={{
                              height: 16,
                              fontSize: '0.5625rem',
                              bgcolor: `${purple[500]}15`,
                              color: purple[600],
                              '& .MuiChip-label': {
                                px: 0.5
                              }
                            }}
                          />
                        )}
                      </Stack>
                    </Box>
                  </Grid>

                  {/* Employee Submission Status */}
                  <Grid item xs={12} sm={1.5}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'text.secondary',
                          mb: 0.5,
                          fontWeight: 500
                        }}
                      >
                        Employee
                      </Typography>
                      <Chip
                        icon={
                          isAccepted ? (
                            <MdLock style={{ fontSize: 12 }} />
                          ) : isSubmitted ? (
                            <MdUpload style={{ fontSize: 12 }} />
                          ) : (
                            <MdSchedule style={{ fontSize: 12 }} />
                          )
                        }
                        label={isSubmitted ? 'Submitted' : 'Pending'}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: '0.5625rem',
                          bgcolor: isAccepted
                            ? `${green[500]}15`
                            : isSubmitted
                            ? `${blue[500]}15`
                            : `${orange[500]}15`,
                          color: isAccepted ? green[500] : isSubmitted ? blue[500] : orange[500],
                          '& .MuiChip-icon': { fontSize: '0.7rem' }
                        }}
                      />
                    </Box>
                  </Grid>

                  {/* Admin Approval Status */}
                  <Grid item xs={12} sm={1.5}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'text.secondary',
                          mb: 0.5,
                          fontWeight: 500
                        }}
                      >
                        Admin
                      </Typography>
                      <Chip
                        icon={
                          isAccepted ? (
                            <MdCheckCircle style={{ fontSize: 12 }} />
                          ) : isSubmitted ? (
                            <MdAdminPanelSettings style={{ fontSize: 12 }} />
                          ) : (
                            <MdClose style={{ fontSize: 12 }} />
                          )
                        }
                        label={isAccepted ? 'Approved' : isSubmitted ? 'Review' : 'Waiting'}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: '0.5625rem',
                          bgcolor: isAccepted
                            ? `${green[500]}15`
                            : isSubmitted
                            ? `${purple[500]}15`
                            : `${grey[500]}15`,
                          color: isAccepted ? green[500] : isSubmitted ? purple[500] : grey[500],
                          '& .MuiChip-icon': { fontSize: '0.7rem' }
                        }}
                      />
                    </Box>
                  </Grid>

                  {/* Actions */}
                  <Grid item xs={12} sm={2.5}>
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      <Tooltip title="Preview Document">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePreviewDocument(document);
                          }}
                          sx={{
                            color: blue[600],
                            '&:hover': { bgcolor: blue[50] }
                          }}
                        >
                          <MdVisibility size={16} />
                        </IconButton>
                      </Tooltip>

                      {document.uploadedFile && document.uploadedFile.filePath && (
                        <Tooltip title="Download Document">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDocumentDownload(document);
                            }}
                            disabled={isDownloadLoading}
                            sx={{
                              color: purple[600],
                              '&:hover': { bgcolor: purple[50] }
                            }}
                          >
                            {isDownloadLoading ? (
                              <CircularProgress size={16} />
                            ) : (
                              <MdDownload size={16} />
                            )}
                          </IconButton>
                        </Tooltip>
                      )}

                      {isAdminLogin && isSubmitted && !isAccepted && (
                        <Tooltip title="Approve Document">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              enhancedHandleDocumentAction(document, 'approve');
                            }}
                            disabled={isActionLoading}
                            sx={{
                              color: green[600],
                              '&:hover': { bgcolor: green[50] }
                            }}
                          >
                            {actionLoading === `approve-${document.documentId}` ? (
                              <CircularProgress size={16} />
                            ) : (
                              <MdThumbUp size={16} />
                            )}
                          </IconButton>
                        </Tooltip>
                      )}

                      {isAdminLogin && (
                        <Tooltip title="More Actions">
                          <IconButton
                            size="small"
                            onClick={(e) => handleActionMenuOpen(e, document)}
                            disabled={acceptDeleteReassignLoading}
                            sx={{
                              color: grey[600],
                              '&:hover': { bgcolor: grey[50] }
                            }}
                          >
                            {acceptDeleteReassignLoading ? (
                              <CircularProgress size={16} />
                            ) : (
                              <MdMoreVert size={16} />
                            )}
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </Grid>
                </Grid>
              </Paper>
            </motion.div>
          );
        })}
      </Box>
    );
  };

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          height: 'calc(100vh - 160px)',
          bgcolor: 'background.paper',
          borderRadius: 2,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* Header with filters */}
        <Box sx={{ p: { xs: 1, sm: 1.5 }, pb: 1 }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems="center"
            spacing={1}
            sx={{ mb: 1 }}
          >
            <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1, minWidth: 0 }}>
              <MdAssignment style={{ color: green[500], fontSize: 20 }} />
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: 'text.primary',
                  minWidth: 0,
                  fontSize: '0.6875rem'
                }}
                noWrap
              >
                Documents
              </Typography>
              <Chip
                label={`${filteredDocuments.length} Found`}
                size="small"
                sx={{
                  height: 18,
                  fontSize: '0.625rem',
                  bgcolor: `${green[500]}15`,
                  color: green[500],
                  '& .MuiChip-label': {
                    px: 0.75
                  }
                }}
              />
            </Stack>

            <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap' }}>
              {selectedCategory && (
                <Chip
                  label={selectedCategory}
                  size="small"
                  sx={{
                    height: 18,
                    fontSize: '0.625rem',
                    bgcolor: `${getCategoryColor(selectedCategory)}15`,
                    color: getCategoryColor(selectedCategory),
                    '& .MuiChip-label': {
                      px: 0.75
                    }
                  }}
                />
              )}
              {selectedSubcategory && (
                <Chip
                  icon={<MdFolder style={{ fontSize: 10 }} />}
                  label={selectedSubcategory}
                  size="small"
                  sx={{
                    height: 18,
                    fontSize: '0.625rem',
                    bgcolor: `${getCategoryColor(selectedCategory)}10`,
                    color: getCategoryColor(selectedCategory),
                    '& .MuiChip-label': {
                      px: 0.75
                    }
                  }}
                />
              )}
            </Stack>
          </Stack>

          {/* Filters */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            alignItems="stretch"
            sx={{ gap: 1 }}
          >
            <TextField
              size="small"
              fullWidth
              variant="outlined"
              placeholder={isMobile ? 'Search...' : 'Search documents or employees...'}
              value={searchTerm}
              onChange={handleSearch}
              sx={{
                maxWidth: isMobile ? '100%' : 300,
                '& .MuiInputBase-root': {
                  fontSize: '0.6875rem',
                  height: 32
                },
                '& .MuiInputBase-input::placeholder': {
                  fontSize: '0.6875rem'
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <MdSearch size={14} color={grey[600]} />
                  </InputAdornment>
                )
              }}
            />

            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel sx={{ fontSize: '0.6875rem' }}>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={handleStatusFilterChange}
                label="Status"
                sx={{
                  fontSize: '0.6875rem',
                  height: 32
                }}
              >
                <MenuItem value="all" sx={{ fontSize: '0.6875rem' }}>
                  All Status
                </MenuItem>
                <MenuItem value="not_submitted" sx={{ fontSize: '0.6875rem' }}>
                  Not Submitted
                </MenuItem>
                <MenuItem value="submitted" sx={{ fontSize: '0.6875rem' }}>
                  Submitted
                </MenuItem>
                <MenuItem value="approved" sx={{ fontSize: '0.6875rem' }}>
                  Approved
                </MenuItem>
              </Select>
            </FormControl>

            <Button
              size="small"
              startIcon={<MdClear size={14} />}
              onClick={handleClearFilters}
              sx={{
                fontSize: '0.625rem',
                color: grey[600],
                minWidth: 60,
                height: 32
              }}
            >
              Clear
            </Button>
          </Stack>
        </Box>

        <Divider sx={{ mx: 1.5, opacity: 0.6 }} />

        {/* Documents Display Area */}
        <Box sx={{ flex: 1, overflow: 'auto', p: 1 }}>
          {filteredDocuments.length > 0 ? (
            isMobile ? (
              // Mobile Card Layout
              <Box>{filteredDocuments.map((document) => renderMobileCard(document))}</Box>
            ) : (
              // Desktop List Layout
              renderDesktopList()
            )
          ) : (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '50%',
                opacity: 0.6
              }}
            >
              <MdAssignment style={{ fontSize: 48, color: grey[400] }} />
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  mt: 1,
                  textAlign: 'center',
                  fontSize: '0.6875rem'
                }}
              >
                {selectedCategory && selectedSubcategory
                  ? `No documents found in "${selectedCategory}" → "${selectedSubcategory}"`
                  : selectedCategory
                  ? `No documents found in "${selectedCategory}"`
                  : 'Select a category to view documents'}
              </Typography>
              {(searchTerm || statusFilter !== 'all') && (
                <Button
                  size="small"
                  onClick={handleClearFilters}
                  sx={{
                    mt: 1,
                    fontSize: '0.625rem'
                  }}
                >
                  Clear Filters
                </Button>
              )}
            </Box>
          )}
        </Box>
      </Paper>

      {/* Snackbar Notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{
          vertical: isMobile ? 'top' : 'bottom',
          horizontal: isMobile ? 'center' : 'right'
        }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default AssignedList;
